import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';

import { aiExperienceContextPluginKey } from './ai-experience-context-plugin-key';
import { reducer } from './reducer';
import type { AIExperienceContextPluginState, AIExperienceContextAction } from './types';

export const initialState: AIExperienceContextPluginState = {
	adfResponse: undefined,
};

/**
 * This plugin will store the core business logic state for the AI experience and eventually
 * replace the xstate machine. The xstate machine is the source of truth for the AI experience
 * until all business logic is migrated to the plugin.
 */
export const createAIExperienceContextPlugin = (options: {
	dispatch: PMPluginFactoryParams['dispatch'];
}): SafePlugin => {
	const { dispatch } = options;
	return new SafePlugin<AIExperienceContextPluginState>({
		key: aiExperienceContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: AIExperienceContextPluginState) => {
				const meta = tr.getMeta(aiExperienceContextPluginKey);
				if (meta) {
					const newState = reducer({
						state: pluginState,
						action: meta as AIExperienceContextAction,
					});
					if (newState !== pluginState) {
						dispatch(aiExperienceContextPluginKey, newState);
					}
					return newState;
				}
				return pluginState;
			},
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): AIExperienceContextPluginState | undefined => {
	return aiExperienceContextPluginKey.getState(state);
};

export const createCommand = (action: AIExperienceContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiExperienceContextPluginKey, action);
	};
};
